import Vue from 'vue'
import VueRouter from 'vue-router'
import Layout from '@/views/layout/Layout'
import store from '@/store'

Vue.use(VueRouter)

/**
 * 供货商端：存在「接单超72小时仍未填报查验信息」的订单时，
 * 只允许停留在查验填报入口页（我的订单），访问其他功能一律拦截并弹窗提示。
 */
const INSPECTION_ALLOWED_PATHS = ['/order/supplier']

function isInspectionAllowed(path) {
  return INSPECTION_ALLOWED_PATHS.some(p => path === p || path.startsWith(p + '/'))
}

const routes = [
  {
    path: '/login',
    component: () => import('@/views/login/index'),
    hidden: true
  },
  {
    path: '/register',
    component: () => import('@/views/register/index'),
    hidden: true
  },
  {
    path: '/nanny-info',
    name: 'NannyInfo',
    component: () => import('@/views/nanny_info/nanny'),
    meta: { title: '人脸采集' },
    hidden: true
  },
  {
    path: '/screen',
    name: 'BigScreen',
    component: () => import('@/views/screen/index'),
    meta: { title: '大屏数据' },
    hidden: true
  },
  {
    path: '/meal-register',
    name: 'MealAccompanimentRegister',
    component: () => import('@/views/meal-accompaniment/register'),
    meta: { title: '陪餐登记' },
    hidden: true
  },
  {
    path: '/',
    component: Layout,
    redirect: '/home',
    // hideMenuFor：菜单中不展示"首页"入口的角色（路由权限仍保留，避免守卫死循环）
    meta: { title: '首页', icon: 'el-icon-house', roles: [1, 2, 3, 4, 5], hideMenuFor: [2, 3] },
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/home/index'),
        meta: { title: '首页', icon: 'el-icon-house', roles: [1, 2, 3, 4, 5] }
      }
    ]
  },
  {
    path: '/mall',
    component: Layout,
    redirect: '/mall/index',
    meta: { title: '食材商城', icon: 'el-icon-goods', roles: [3, 5] },
    children: [
      {
        path: 'index',
        name: 'FoodMall',
        component: () => import('@/views/mall/index'),
        meta: { title: '食材商城', icon: 'el-icon-goods', roles: [3, 5] }
      },
      {
        path: 'products/:id',
        name: 'ProductDetail',
        component: () => import('@/views/mall/product-detail'),
        meta: { title: '商品详情', roles: [3, 5] },
        hidden: true
      }
    ]
  },
  {
    path: '/supplier',
    component: Layout,
    // 默认跳转到第一个有权限的子页
    // 平台/供应商端：/supplier/list（供应商列表）
    // 仓库端：/supplier/vehicle（车辆管理）
    redirect: '/supplier/list',
    meta: { title: '供应商管理', icon: 'el-icon-s-custom', roles: [1, 2, 4, 5] },
    children: [
      {
        path: 'list',
        name: 'SupplierList',
        component: () => import('@/views/supplier/list'),
        meta: { title: '供应商列表', roles: [1, 5] }
      },
      {
        path: 'audit/:id?',
        name: 'SupplierAudit',
        component: () => import('@/views/supplier/audit'),
        meta: { title: '供应商审核', roles: [1, 5] }
      },
      {
        path: 'detail/:id',
        name: 'SupplierDetail',
        component: () => import('@/views/supplier/detail'),
        meta: { title: '供应商详情', roles: [1, 2, 5] },
        hidden: true
      },
      {
        path: 'add',
        name: 'SupplierAdd',
        component: () => import('@/views/supplier/add'),
        meta: { title: '新增供应商', roles: [1] },
        hidden: true
      },
      {
        path: 'edit/:id',
        name: 'SupplierEdit',
        component: () => import('@/views/supplier/edit'),
        meta: { title: '编辑供应商', roles: [1] },
        hidden: true
      },
      {
        path: 'vehicle',
        name: 'VehicleList',
        component: () => import('@/views/vehicle/list'),
        meta: { title: '车辆管理', roles: [1, 2, 4, 5] }
      },
      {
        path: 'delivery-man',
        name: 'DeliveryManList',
        component: () => import('@/views/delivery-man/list'),
        meta: { title: '配送员管理', roles: [1, 2, 4, 5] }
      }
    ]
  },
  {
    path: '/product',
    component: Layout,
    redirect: '/product/list',
    meta: { title: '商品管理', icon: 'el-icon-goods', roles: [1, 2, 5] },
    children: [
      {
        path: 'list',
        name: 'ProductList',
        component: () => import('@/views/product/list'),
        meta: { title: '商品列表', roles: [1, 2, 5] }
      },
      {
        path: 'publish',
        name: 'ProductPublish',
        component: () => import('@/views/product/publish'),
        meta: { title: '发布商品', roles: [1, 2] },
        hidden: true
      },
      {
        path: 'edit/:id',
        name: 'ProductEdit',
        component: () => import('@/views/product/publish'),
        meta: { title: '编辑商品', roles: [1, 2] },
        hidden: true
      },
      {
        path: 'audit',
        name: 'ProductAudit',
        component: () => import('@/views/product/audit'),
        meta: { title: '商品审核', titleMap: { 2: '审核中的商品' }, roles: [1, 2] }
      },
      {
        path: 'category',
        name: 'ProductCategory',
        component: () => import('@/views/product/category'),
        meta: { title: '商品分类', roles: [1, 5] }
      },
      {
        path: 'brand',
        name: 'ProductBrand',
        component: () => import('@/views/product/brand'),
        meta: { title: '商品品牌', roles: [1] }
      },
      {
        path: 'qualification-manage',
        name: 'BrandQualificationManage',
        component: () => import('@/views/brand-qualification/manage'),
        meta: { title: '品牌资质管理', roles: [1] }
      },
      {
        path: 'qualification-audit',
        name: 'BrandQualificationAudit',
        component: () => import('@/views/brand-qualification/audit'),
        meta: { title: '品牌资质审核', roles: [1] }
      },
      {
        path: 'qualification-reminder',
        name: 'BrandQualificationReminder',
        component: () => import('@/views/brand-qualification/reminder'),
        meta: { title: '品牌过期提醒', roles: [1, 2] }
      },
      {
        path: 'qualification-my',
        name: 'BrandQualificationMy',
        component: () => import('@/views/brand-qualification/my'),
        meta: { title: '我的品牌资质', roles: [2] }
      },
      {
        path: 'banner',
        name: 'BannerManage',
        component: () => import('@/views/mall/banner'),
        meta: { title: '商城轮播图', roles: [1] }
      },
      {
        path: 'unit',
        name: 'ProductUnit',
        component: () => import('@/views/product/unit'),
        meta: { title: '单位管理', roles: [1] }
      }
    ]
  },
  {
    path: '/bid-bargain',
    component: Layout,
    redirect: '/bid-bargain/index',
    // roles 含 5：区教育局端可见"在线议价"菜单（只读历史记录）；含 3：学校端可见"竞价一览表"
    meta: { title: '在线议价', icon: 'el-icon-money', roles: [1, 2, 3, 5] },
    children: [
      {
        path: 'index',
        name: 'BidPlatformIndex',
        component: () => import('@/views/bid-bargain/platform/index'),
        meta: { title: '在线议价', roles: [1, 5] }
      },
      {
        path: 'price-matrix',
        name: 'BidPriceMatrix',
        component: () => import('@/views/bid-bargain/bid-price-matrix'),
        meta: { title: '竞价一览表', roles: [1, 3, 5] }
      },
      {
        path: 'product',
        name: 'BidProduct',
        component: () => import('@/views/bid-bargain/bid-product'),
        meta: { title: '议价商品表', roles: [1] }
      },
      {
        path: 'product/add',
        name: 'BidProductAdd',
        component: () => import('@/views/bid-bargain/bid-product-edit'),
        meta: { title: '新增竞价商品', roles: [1] },
        hidden: true
      },
      {
        path: 'product/edit/:id',
        name: 'BidProductEdit',
        component: () => import('@/views/bid-bargain/bid-product-edit'),
        meta: { title: '编辑竞价商品', roles: [1] },
        hidden: true
      },
      {
        path: 'supplier/countdown',
        name: 'BidSupplierCountdown',
        component: () => import('@/views/bid-bargain/supplier/countdown'),
        meta: { title: '在线议价', roles: [2] }
      },
      {
        path: 'supplier/quote/:taskId',
        name: 'BidSupplierQuote',
        component: () => import('@/views/bid-bargain/supplier/quote'),
        meta: { title: '供应商报价', roles: [2] },
        hidden: true
      },
      {
        path: 'supplier/history/:taskId',
        name: 'BidSupplierHistory',
        component: () => import('@/views/bid-bargain/supplier/history'),
        meta: { title: '议价记录详情', roles: [2] },
        hidden: true
      },
      {
        path: 'award/:taskId',
        name: 'BidAward',
        component: () => import('@/views/bid-bargain/platform/award'),
        // 定标页：不给教育局(5)权限，教育局无定标权限
        meta: { title: '平台定标', roles: [1] },
        hidden: true
      },
      {
        path: 'history/:taskId',
        name: 'BidHistory',
        component: () => import('@/views/bid-bargain/platform/history'),
        // 议价记录：教育局只读查看
        meta: { title: '议价记录', roles: [1, 5] },
        hidden: true
      }
    ]
  },
  {
    path: '/purchase',
    component: Layout,
    redirect: '/purchase/unit',
    meta: { title: '采购管理', icon: 'el-icon-s-order', roles: [1, 3] },
    children: [
      {
        path: 'unit',
        name: 'PurchasingUnitList',
        component: () => import('@/views/purchasing-unit/list'),
        meta: { title: '采购单位管理', roles: [1] }
      },
      {
        path: 'unit/add',
        name: 'PurchasingUnitAdd',
        component: () => import('@/views/purchasing-unit/add'),
        meta: { title: '新增采购单位', roles: [1] },
        hidden: true
      },
      {
        path: 'unit/edit/:id',
        name: 'PurchasingUnitEdit',
        component: () => import('@/views/purchasing-unit/add'),
        meta: { title: '编辑采购单位', roles: [1] },
        hidden: true
      },
      {
        path: 'plan',
        name: 'PurchasePlanList',
        component: () => import('@/views/purchase-plan/list'),
        meta: { title: '采购计划', roles: [1, 3] }
      },
      {
        path: 'plan/add',
        name: 'PurchasePlanAdd',
        component: () => import('@/views/purchase-plan/add'),
        meta: { title: '新增采购计划', roles: [1, 3] },
        hidden: true
      },
      {
        path: 'plan/detail/:id',
        name: 'PurchasePlanDetail',
        component: () => import('@/views/purchase-plan/detail'),
        meta: { title: '采购计划详情', roles: [1, 3] },
        hidden: true
      },
      {
        path: 'plan/success',
        name: 'PurchasePlanSuccess',
        component: () => import('@/views/purchase-plan/success'),
        meta: { title: '提交成功', roles: [1, 3] },
        hidden: true
      },
      {
        path: 'food-sample',
        name: 'FoodSampleList',
        component: () => import('@/views/food-sample/list'),
        meta: { title: '留样管理', roles: [1, 3] }
      },
      {
        path: 'meal-accompaniment',
        name: 'MealAccompanimentList',
        component: () => import('@/views/meal-accompaniment/list'),
        meta: { title: '陪餐管理', roles: [1, 3] }
      }
    ]
  },
  {
    path: '/stock',
    component: Layout,
    redirect: '/stock/manage',
    meta: { title: '商品出入库', icon: 'el-icon-box', roles: [3] },
    children: [
      {
        path: 'manage',
        name: 'StockManage',
        component: () => import('@/views/stock/list'),
        meta: { title: '库存管理', roles: [3] }
      },
      /*{
        path: 'outbound-record',
        name: 'OutboundRecord',
        component: () => import('@/views/stock/outbound-record'),
        meta: { title: '出库记录汇总', roles: [3] }
      },
      {
        path: 'inbound-record',
        name: 'InboundRecord',
        component: () => import('@/views/stock/inbound-record'),
        meta: { title: '入库记录汇总', roles: [3] }
      },*/
      {
        path: 'order-inbound-summary',
        name: 'OrderInboundSummary',
        component: () => import('@/views/stock/order-inbound-summary'),
        meta: { title: '订单入库记录汇总', roles: [3] }
      },
      {
        path: 'outbound-summary',
        name: 'OutboundSummary',
        component: () => import('@/views/stock/outbound-summary'),
        meta: { title: '订单出库记录汇总', roles: [3] }
      },
      {
        path: 'report',
        name: 'StockReport',
        component: () => import('@/views/stock/report'),
        meta: { title: '库存报表', roles: [3] }
      },
      /*{
        path: 'repair',
        name: 'StockRepair',
        component: () => import('@/views/stock/repair'),
        meta: { title: '数据修复', roles: [3] }
      }*/
    ]
  },
  {
    path: '/order',
    component: Layout,
    redirect: '/order/sales',
    meta: { title: '订单管理', icon: 'el-icon-s-shop', roles: [1, 2, 3, 4, 5] },
    children: [
      {
        path: 'list',
        name: 'OrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '订单列表', roles: [3] }
      },
      {
        path: 'purchase',
        name: 'PurchaseOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '采购订单管理', roles: [] }
      },
      {
        path: 'sales',
        name: 'SalesOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '销售订单管理', roles: [1, 4, 5] }
      },
      {
        path: 'platform-purchase',
        name: 'PlatformPurchaseOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '平台采购订单', roles: [1, 4, 5] }
      },
      {
        path: 'supplier',
        name: 'SupplierOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '我的订单', roles: [2] }
      },
      {
        path: 'acceptance/:id',
        name: 'OrderAcceptance',
        component: () => import('@/views/order/acceptance'),
        meta: { title: '订单验收', roles: [1, 3] },
        hidden: true
      }
    ]
  },
  {
    path: '/statistics',
    component: Layout,
    redirect: '/statistics/supplier',
    meta: { title: '数据统计', icon: 'el-icon-s-data', roles: [1, 5] },
    children: [
      {
        path: 'supplier',
        name: 'SupplierSalesReport',
        component: () => import('@/views/statistics/list'),
        meta: { title: '供货商销售报表', roles: [1, 5] }
      },
      {
        path: 'school',
        name: 'SchoolPurchaseReport',
        component: () => import('@/views/statistics/list'),
        meta: { title: '学校采购报表', roles: [1, 5] }
      }
    ]
  },
  {
    path: '/finance-reconciliation',
    component: Layout,
    redirect: '/finance-reconciliation/list',
    meta: { title: '财务对账', icon: 'el-icon-s-finance', roles: [1, 2, 3, 5] },
    children: [
      {
        path: 'list',
        name: 'FinanceReconciliationList',
        component: () => import('@/views/finance-reconciliation/list'),
        meta: { title: '对账单', roles: [1, 2, 3, 5] }
      },
      {
        path: 'detail/:id',
        name: 'FinanceReconciliationDetail',
        component: () => import('@/views/finance-reconciliation/detail'),
        meta: { title: '对账单详情', roles: [1, 2, 3, 5] },
        hidden: true
      }
    ]
  },
  {
    path: '/Education-purchase',
    component: Layout,
    redirect: '/Education-purchase/list',
    meta: { title: '采购单位', icon: 'el-icon-s-flag', roles: [5] },
    children: [
      {
        path: 'list',
        name: 'EduPurchaseOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '订单列表', roles: [5] }
      },
      {
        path: 'food-sample',
        name: 'EduFoodSampleList',
        component: () => import('@/views/food-sample/list'),
        meta: { title: '留样管理', roles: [5] }
      },
      {
        path: 'meal-accompaniment',
        name: 'EduMealAccompanimentList',
        component: () => import('@/views/meal-accompaniment/list'),
        meta: { title: '陪餐管理', roles: [5] }
      }
    ]
  },
  {
    path: '/Education-platform',
    component: Layout,
    redirect: '/Education-platform/platform-purchase',
    meta: { title: '集采平台', icon: 'el-icon-s-flag', roles: [5] },
    children: [
      {
        path: 'platform-purchase',
        name: 'EduPlatformPurchaseOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '平台采购订单', roles: [5] }
      },
      {
        path: 'vehicle',
        name: 'EduVehicleList',
        component: () => import('@/views/vehicle/list'),
        meta: { title: '车辆管理', roles: [5] }
      },
      {
        path: 'delivery-man',
        name: 'EduDeliveryManList',
        component: () => import('@/views/delivery-man/list'),
        meta: { title: '配送员管理', roles: [5] }
      }
    ]
  },
  {
    path: '/edu-product',
    component: Layout,
    redirect: '/edu-product/list',
    meta: { title: '商品管理', icon: 'el-icon-goods', roles: [5] },
    children: [
      {
        path: 'list',
        name: 'EduProductList',
        component: () => import('@/views/product/list-readonly'),
        meta: { title: '商品列表', roles: [5] }
      }
    ]
  },
  {
    path: '/sys-config',
    component: Layout,
    redirect: '/sys-config/edu-price',
    meta: { title: '系统设置', icon: 'el-icon-setting', roles: [1] },
    children: [
      {
        path: 'edu-price',
        name: 'SysConfigEduPrice',
        component: () => import('@/views/sys-config/edu-price'),
        meta: { title: '价格可见性', roles: [1] }
      },
      {
        path: 'date-picker-demo',
        name: 'SysConfigDatePickerDemo',
        component: () => import('@/views/sys-config/date-picker-demo'),
        meta: { title: '日期控件预览', roles: [1] }
      }
    ]
  },
  {
    path: '/Education-supplier',
    component: Layout,
    redirect: '/Education-supplier/supplier',
    meta: { title: '供货单位', icon: 'el-icon-s-flag', roles: [5] },
    children: [
      {
        path: 'supplier',
        name: 'EduSupplierOrderList',
        component: () => import('@/views/order/list'),
        meta: { title: '我的订单', roles: [5] }
      },
      {
        path: 'vehicle',
        name: 'EduSupplierVehicleList',
        component: () => import('@/views/vehicle/list'),
        meta: { title: '车辆管理', roles: [5] }
      },
      {
        path: 'delivery-man',
        name: 'EduSupplierDeliveryManList',
        component: () => import('@/views/delivery-man/list'),
        meta: { title: '配送员管理', roles: [5] }
      }
    ]
  },
  {
    path: '/device',
    component: Layout,
    redirect: '/device/list',
    meta: { title: '智慧物联', icon: 'el-icon-video-camera', roles: [1, 5] },
    children: [
      // {
      //   path: 'live-list',
      //   name: 'LiveList',
      //   component: () => import('@/views/device/live-list'),
      //   meta: { title: '直播列表', roles: [1, 5] }
      // },
      {
        path: 'live-view',
        name: 'LiveView',
        component: () => import('@/views/device/live-view'),
        meta: { title: '直播视图', roles: [1, 5] }
      },
      {
        path: 'list',
        name: 'DeviceList',
        component: () => import('@/views/device/list'),
        meta: { title: '设备管理', roles: [1, 5] }
      }
    ]
  },
  {
    path: '/traceability-report',
    component: Layout,
    redirect: '/traceability-report/list',
    meta: { title: '溯源报告', icon: 'el-icon-document-copy', roles: [1, 3, 4, 5] },
    children: [
      {
        path: 'list',
        name: 'TraceabilityReportList',
        component: () => import('@/views/traceability-report/list'),
        meta: { title: '溯源报告', roles: [1, 3, 4, 5] }
      }
    ]
  },
  {
    path: '/system',
    component: Layout,
    redirect: '/system/user',
    meta: { title: '系统管理', icon: 'el-icon-s-tools', roles: [1, 5] },
    children: [
      {
        path: 'user',
        name: 'SysUserList',
        component: () => import('@/views/sys-user/list'),
        meta: { title: '账号管理', roles: [1, 5] }
      },
      {
        path: 'platform-unit',
        name: 'PlatformUnitList',
        component: () => import('@/views/platform-unit/list'),
        // cityOnly：仅市级单位（平台监管 或 区县为空的集采平台账号）可见，由 Layout 动态过滤
        meta: { title: '平台单位管理', roles: [1, 5], cityOnly: true }
      }
    ]
  }
]

// 静默处理导航中止/重复导航异常：
// 守卫 next(false)（如供货商逾期查验拦截）会让调用方的 router.push 返回 rejected Promise，
// 调用方未捕获时会在 webpack dev overlay 报 "Uncaught runtime errors: Navigation aborted"
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch(err => err)
}
const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch(err => err)
}

const router = new VueRouter({
  routes
})

// 各角色默认首页（用于登录后跳转 / 无权限时的 fallback）
// 供货商(2) -> 我的订单；采购单位(3) -> 订单列表；其余 -> 首页大屏
function defaultHome(userType) {
  if (userType === 2) return '/order/supplier'
  if (userType === 3) return '/order/list'
  return '/home'
}

// 路由守卫：登录拦截 + 角色/区域/竞价开关校验（防止直接输入 URL 绕过菜单隐藏）
// 供货商端额外校验：存在逾期未填报查验信息的订单时，拦截非填报入口的跳转
router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('token')
  // 公开页面：登录页 / 注册页 / 陪餐登记页（链接携带 schId，无需登录）
  const PUBLIC_PATHS = ['/login', '/register', '/meal-register']
  if (!token && !PUBLIC_PATHS.includes(to.path)) {
    next('/login')
    return
  }
  if (token && (to.path === '/login' || to.path === '/register')) {
    next(defaultHome(Number(localStorage.getItem('userType') || 0)))
    return
  }
  if (token) {
    const userType = Number(localStorage.getItem('userType') || 0)
    // 角色校验：路由链上任一节点的 meta.roles 不含当前身份则拒绝
    const deniedByRole = to.matched.some(r => {
      const roles = r.meta && r.meta.roles
      return roles && roles.length && !roles.includes(userType)
    })
    // 市级专属页面（如平台单位管理）：区县为空的平台监管/集采平台账号才可访问
    const district = localStorage.getItem('district') || ''
    const districtCode = localStorage.getItem('districtCode') || ''
    const isCityLevel = (userType === 1 || userType === 5) && !district && !districtCode
    const deniedByCity = to.matched.some(r => r.meta && r.meta.cityOnly && !isCityLevel)
    // 竞价开关：区县未开启竞价时禁止直达在线议价模块
    const deniedByBid = to.path.startsWith('/bid-bargain') && localStorage.getItem('enableBid') === '0'
    if (deniedByRole || deniedByCity || deniedByBid) {
      next(defaultHome(userType))
      return
    }
    // 供货商端：存在逾期未填报查验信息的订单时，拦截其他功能并弹出提示
    if (userType === 2) {
      try {
        await store.dispatch('fetchOverdueInspections')
      } catch (e) {
        // 查询失败不阻塞导航，避免网络异常导致无法使用系统
      }
      if ((store.state.overdueInspections || []).length > 0 && !isInspectionAllowed(to.path)) {
        // 注意：这里不能用 next(false)。页面首次加载时取消导航会导致没有任何匹配路由，
        // 出现白屏；且强制弹窗挂在 Layout 下，Layout 未挂载时弹窗也不会显示。
        // 改为重定向到「我的订单」（允许路径，二次进守卫会直接放行，不会死循环），
        // Layout 正常渲染后强制填报弹窗自动弹出。
        store.commit('SET_OVERDUE_DIALOG_VISIBLE', true)
        if (to.path === INSPECTION_ALLOWED_PATHS[0]) {
          next()
        } else {
          next(INSPECTION_ALLOWED_PATHS[0])
        }
        return
      }
    }
  }
  next()
})

export default router
